import { Component, Inject, Input, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { Router } from '@angular/router';
import { CbreAlertService } from '@cbre/alerts';
import { environment } from '@env/environment';
import { TRANSLATOR, Translator } from '@miq/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EntityType } from '@shared/models/entity-enum';
import { IOption, IOptionDropdown } from '@shared/models/icommons.model';
import { DEPRECATED_LANGUAGES, LanguageLocale } from '@shared/models/language.model';
import { ALL } from '@shared/models/strings.constants';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import {
  isCustomAllOptionSelected,
  getSearchStateFormattedToBeSave,
} from '@shared/services/clipboard-search-state.helper';
import { ClipboardSearchConfigByEntity } from '@shared/services/clipboard-search-state.model';
import { ClipboardSearchStateService } from '@shared/services/clipboard-search-state.service';
import { DataService } from '@shared/services/data.service';
import { SharedService } from '@shared/services/shared.service';
import { UserService } from '@user/services/user.service';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { from, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { IExportLimits, IUser, IUserRequest, UserGroups, UserType } from '@shared/models/iuser.model';
import { AdminService } from '@admin/services/admin.service';
import { TogglePointFunctionHelper } from '@cbre/feature-toggles';
import { DomSanitizer } from '@angular/platform-browser';
import { ReactEmbeddedVersionService } from '@shared/components/react-embed/react-embedded.service';

@UntilDestroy()
@Component({
  selector: 'miq-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false,
})
export class NavbarComponent implements OnDestroy, OnInit {
  @Input() public appName: string;
  @Input() public userName: string;
  @Input() public clipboardLength: number;
  @Input() public isRequestPage: boolean;

  public entities$: Observable<IOptionDropdown[]>;
  public totalClipboardItems = 0;
  public languages$: Observable<Array<LanguageLocale & { language?: string }>>;
  public howToVideosText: string;
  public howToVideosURL: string;
  public userInformation: IUser;
  public isAdmin = false;
  public countryLobData: any[];
  public groupsData: any[];
  public exportLimits: IExportLimits[];
  roles: IOption[];
  private userId: number;
  private userCountrySkey: number;
  public isDigitalAssistantUser$: Observable<boolean>;
  private isSpacesViewEnabledFlag = false;

  constructor(
    userInfoResolver: UserInfoResolver,
    private sharedService: SharedService,
    private dataService: DataService,
    private router: Router,
    private userService: UserService,
    private cbreAlertService: CbreAlertService,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    @Inject(TRANSLATOR) translation: Translator,
    private clipboardSearchStateService: ClipboardSearchStateService,
    private adminHttpService: AdminService,
    private sanitizer: DomSanitizer,
    private reactVersionService: ReactEmbeddedVersionService
  ) {
    this.userId = userInfoResolver.resolve().id;
    this.userCountrySkey = userInfoResolver.resolve().userCountrySkey;

    this.howToVideosText = translation.translate('admin.miqDocumentation');
    this.howToVideosURL = environment.howToVideosURL;
    this.isDigitalAssistantUser$ = from(userInfoResolver.isDigitalAssistantUser());
  }

  #updateClipboarSearchSate = (entities: IOptionDropdown[]) => {
    const stateSearch = this.clipboardSearchStateService.getSearchConfigs(
      EntityType.Property
    ) as ClipboardSearchConfigByEntity;
    if (isCustomAllOptionSelected(stateSearch)) {
      return;
    }
    const propertyClipboardCount = entities.find(e => e.key === EntityType.Property)?.value || 0;
    if (propertyClipboardCount === 0) {
      const newSearchState = getSearchStateFormattedToBeSave([], stateSearch, EntityType.Property);
      this.clipboardSearchStateService.updateSearchConfigs(EntityType.Property, newSearchState, true);
    }
  };

  ngOnInit() {
    /* required by Angular-l10n */
    this.isSpacesViewEnabledFlag = this.showSpacesViewFeatureFlag();
    // setup language/locale
    this.languages$ = this._getLanguagesAndLocales$();
    // clipboard menu navigation
    this.entities$ = this._getEntitiesAndCountClipboardTotal();

    // recalculate total clipboard items
    this.dataService
      .getNewCBRecord()
      .pipe(
        untilDestroyed(this),
        switchMap(this._getEntitiesAndCountClipboardTotal.bind(this)),
        tap(() => (this.entities$ = this._getEntitiesAndCountClipboardTotal())),
        tap(this.#updateClipboarSearchSate)
      )
      .subscribe();

    // clipboard navigation
    this.dataService
      .getClipboardEntity()
      .pipe(untilDestroyed(this))
      .subscribe(clipboardEntity => this.navigateToClipboardEntity(clipboardEntity));
    this.getUserInformation();
    this._getUserRoles();
    this._getCountryLob();
    this._getGroups();
  }

  ngOnDestroy() {
    /* required by Angular-l10n */
  }

  private showSpacesViewFeatureFlag = () => {
    return TogglePointFunctionHelper.toggleRouterService?.isFeatureEnabled?.('spacesViewEnabled');
  };

  private formatDataStackVersion(version: string): string {
    const trimmed = (version || '').trim();
    if (!trimmed) {
      return 'x.x.x';
    }

    // Normalize tag-like versions (e.g., rc/v26.5.29-rc.16 -> 26.5.29-rc.16)
    const lastPathSegment = trimmed.split('/').pop() || trimmed;
    return lastPathSegment.startsWith('v') ? lastPathSegment.slice(1) : lastPathSegment;
  }

  /**
   * Gets the all possible user languages
   * */
  private _getLanguagesAndLocales$() {
    return this.userService.getUserLanguages().pipe(
      map(languages =>
        languages
          .map(lang => ({ ...lang, countryFlagKey: lang.languageCode.split('_').pop() }))
          // filter  the deprecated languages
          .filter(lang => !DEPRECATED_LANGUAGES.includes(lang.localeKey))
      )
    );
  }

  /**
   * Loads entities and calculates total clipboard items
   * @returns
   */
  private _getEntitiesAndCountClipboardTotal() {
    return (
      this.sharedService
        .getEntities(this.userId)
        // sum and set total clipboard items
        .pipe(tap(entities => (this.totalClipboardItems = this._sumClipboardQuantityByEntity(entities))))
    );
  }

  private _sumClipboardQuantityByEntity(entities: IOptionDropdown[]): number {
    return entities.reduce((acc, { value }) => acc + value, 0);
  }

  private navigateToClipboardEntity(entity) {
    this.router.navigate(['/records/clipboard'], { queryParams: { entity } });
  }

  /**
   * Method reacting when user clicks on clear one entity
   * @param entity
   */
  public onClearEntity(entity: string) {
    this.sharedService
      .deleteEntityRecords(this.userId, entity.toLowerCase(), this.isSpacesViewEnabledFlag)
      .subscribe(this.onClearEntityEvent.bind(this, entity));
  }

  /**
   * Method reacting to clear all clipboard entities
   */
  public clearAll() {
    this.sharedService.clearClipboard(this.userId).subscribe(this.onClearAllSuccess.bind(this));
  }

  /**
   * "Clear one entity" response handler
   * @param entity
   */
  private onClearEntityEvent(entity: string) {
    this.dataService.setNewCBRecord();
    this.dataService.sendClearEntity(entity);
  }

  /**
   * "Clear all" response handler
   */
  private onClearAllSuccess() {
    this.onClearEntityEvent(ALL);
    this.dataService.setNewCBRecord();
  }

  /**
   * Triggers the navigate to given entity clipboard process
   * @param entity
   */
  public onClipboardClick(entity: string) {
    this.dataService.notifyClipboardEntity(entity);
  }

  /**
   * Set selected language and reload
   * @param localeSkey
   */
  public setDefaultLang(localeSkey: number) {
    this.userService.setDefaultLang(this.userId, localeSkey).subscribe(() => location.reload());
  }

  /**
   * Displays the current MIQ version or Data Stack version depending on the route
   */
  public showVersion(): void {
    const url = this.router?.url ?? '';
    const isDataStackRoute = url.startsWith('/data-stack');
    const DEFAULT_APP_NAME = 'MarketIQ';
    const DEFAULT_VERSION = 'x.x.x';
    const DEFAULT_DATASTACK_NAME = 'Data Stack';

    if (isDataStackRoute) {
      const kadsBaseUrl = (environment as any)?.KADS?.FEPrefix;

      if (!kadsBaseUrl) {
        this.cbreAlertService.showInfoSnackBar(`${DEFAULT_DATASTACK_NAME.toUpperCase()} ${DEFAULT_VERSION}`);
        return;
      }

      const cachedVersion = this.reactVersionService.displayVersion(kadsBaseUrl);
      const resolvedVersion = this.formatDataStackVersion(cachedVersion ?? DEFAULT_VERSION);
      this.cbreAlertService.showInfoSnackBar(`${DEFAULT_DATASTACK_NAME.toUpperCase()} ${resolvedVersion}`);
      return;
    }

    const version = environment?.appVersion ?? DEFAULT_VERSION;
    const rawDisplayName = environment?.appName ?? DEFAULT_APP_NAME;
    const displayName = (rawDisplayName || '').trim();
    const message = displayName ? `${displayName.toUpperCase()} ${version}` : version;
    this.cbreAlertService.showInfoSnackBar(message);
  }

  /**
   * Removes user token from local storage and replaces the url with configured logout url
   */
  public logOut() {
    localStorage.removeItem('userid_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('expires_in');
    const sanitizedURL = this.sanitizer.sanitize(SecurityContext.URL, environment.baseLogOutURL + environment.host);
    window.location.href = sanitizedURL;
  }
  openLinkInNewTab(url: string) {
    if (this.isAdmin) {
      window.open(url, '_blank');
      return;
    }
    window.open(url, '_self');
  }

  private _getUserRoles() {
    this.adminHttpService
      .getRoles()
      .pipe(untilDestroyed(this))
      .subscribe((roles: IOption[]) => {
        this.roles = roles;
      });
  }

  public getUserInformation(): void {
    this.userService
      .getUsers(this.userId)
      .pipe(take(1), untilDestroyed(this))
      .subscribe(result => {
        this.userInformation = result;
        this.isAdmin =
          this.userInformation?.userTypeId === UserType.ADMIN ||
          this.userInformation?.groups?.some(group => group.value === 'Admin User');
      });
  }

  private _getCountryLob() {
    this.adminHttpService
      .getLobList(this.userCountrySkey)
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.countryLobData = data;
      });
  }

  private _getGroups() {
    this.adminHttpService
      .userGroups()
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.groupsData = response;
      });
  }

  private createUserRequest(updateFields: Partial<IUser>): IUserRequest {
    return {
      user: {
        ...this.userInformation,
        ...updateFields,
        updateBy: this.userInformation.emailAddress,
      },
    };
  }

  updateUserType(type: 'role' | 'lob', value: number) {
    const updateFields = type === 'role' ? { roleId: value } : { userLobSkey: value };
    this._updateUser(updateFields);
  }

  updateUserGroups(groups: UserGroups[]) {
    this._updateUser({ groups });
  }

  private _updateUser(requestParams) {
    const userRequest = this.createUserRequest(requestParams);
    const EDIT_ADDRESS_GROUP_KEY = 17;
    const TRI_STATE_RESEARCH_LOB_KEY = 104;

    if (userRequest.user.userLobSkey != TRI_STATE_RESEARCH_LOB_KEY) {
      userRequest.user.groups = userRequest.user.groups.filter(group => group.key !== EDIT_ADDRESS_GROUP_KEY);
    }

    this.adminHttpService.updateUser(userRequest).subscribe(
      () => this.getUserInformation(),
      () => this.cbreAlertService.showInfoSnackBar_l10n('admin.userUpdateFailed')
    );
  }
}
